import { GeneralApiService } from '@api_patient/general_api.service';
import { AnalyticCategories } from '@application/analytic/analytic.service';
import { AnalyticProvider } from '@application/analytic/analytic_link/mod';
import { withTranslation } from '@application/i18n/with_translation';
import { Button } from '@cellula/react';
import { Async } from '@component/async.component';
import { PrimitiveVonageEncryptionWarning, useVonageEncryptionWarning } from '@component/vonage_encryption_warning/mod';
import { useResolve, useResolveController, withNewContainer } from '@visiba/instantiation';
import { Timer } from '@visiba/std';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { styled } from 'styled-components';
import { ScreenDone } from './screen_done/mod';
import { ScreenEvaluation } from './screen_evaluation/mod';
import { SingleForm } from './screen_form/mod';
import { ScreenMissingPayment } from './screen_missing_payment/mod';
import { ScreenReconnect } from './screen_reconnect/screen_reconnect.component';
import { ScreenVideo } from './screen_video/mod';
import { AppointmentController } from './view_appointment.controller';
import { ViewAppointmentState } from './view_appointment_state.component';

const Container = styled.div`
	display: flex;
	flex: 1;
	overflow: hidden;
	position: relative;
	height: 100%;
	width: 100%;
`;

interface Props {}

export const ViewAppointment = withNewContainer(function ViewAppointment(_: Props) {
	const { ticket } = useParams();
	const controller = useResolveController(AppointmentController, ticket);
	const generalApiService = useResolve(GeneralApiService);
	const { hasEncryptionSupport, appConfig } = useVonageEncryptionWarning();

	useEffect(() => {
		return Timer.repeat(() => {
			generalApiService.touchSession();
		}, 1000 * 60);
	}, [generalApiService]);

	return (
		<AnalyticProvider category={AnalyticCategories.PatientCall}>
			<Container>
				<Async
					derive={controller.state}
					catch={<ViewAppointmentState title={withTranslation('PW.Common.Error.Unknown')} illustration='disconnected' />}
				>
					{(state) => {
						if (controller.appointmentInfo.value?.vonageEncryptionRequired && !hasEncryptionSupport) {
							return (
								<ViewAppointmentState
									title={<PrimitiveVonageEncryptionWarning.Title />}
									description={<PrimitiveVonageEncryptionWarning.Text type='dialog' />}
									illustration='disconnected'
									actions={
										appConfig?.appDownloadUrl ? (
											<Button as='a' href={appConfig.appDownloadUrl}>
												{withTranslation('VonageEncryptionWarning.Dialog.ButtonDownloadApp')}
											</Button>
										) : null
									}
								/>
							);
						}
						if (state.isMissingPayment) {
							return <ScreenMissingPayment ticket={ticket} />;
						}

						// Keeping this if we ever decides that it was indeed good that the before form is present here.
						// if (state.beforeForm != null) {
						// 	return <SingleForm worker={state.beforeForm} />;
						// }

						if (state.reconnectVariants != null) {
							return <ScreenReconnect type={state.reconnectVariants} />;
						}

						if (state.shouldEvaluate === 'not_completed') {
							return <ScreenEvaluation />;
						}

						if (state.afterForm != null) {
							return <SingleForm worker={state.afterForm} />;
						}

						if (state.shouldEvaluate === 'completed') {
							return <ScreenDone />;
						}

						return <ScreenVideo />;
					}}
				</Async>
			</Container>
		</AnalyticProvider>
	);
});
